import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import gph from "../../../assets/images/logo-georanking.png";

const topics = [
  { id: "qualificacao", title: "Qualificação das Partes" },
  { id: "clausula-1", title: "1. Objeto" },
  { id: "clausula-2", title: "2. Aceitação do Serviço" },
  { id: "clausula-3", title: "3. Período de Cobrança" },
  { id: "clausula-4", title: "4. Período de Cortesia" },
  { id: "clausula-5", title: "5. Forma de Reajustes" },
  { id: "clausula-6", title: "6. Intermediação e Taxa de Adesão" },
  { id: "clausula-7", title: "7. Cancelamento" },
  { id: "clausula-8", title: "8. Remoção de Propriedade Intelectual" },
  { id: "clausula-9", title: "9. Desconexão Antecipada e Penalidade" },
  { id: "clausula-10", title: "10. Proibições de Uso" },
  { id: "clausula-11", title: "11. Limitação de Responsabilidade" },
  { id: "clausula-12", title: "12. Disposições Gerais" },
  { id: "clausula-13", title: "13. Foro e Resolução de Conflitos" },
  { id: "clausula-14", title: "14. Canal de Suporte" },
  { id: "clausula-15", title: "15. Divulgação de Resultados" },
  { id: "clausula-16", title: "16. Propriedade Intelectual" },
  { id: "clausula-17", title: "17. Política de Privacidade e LGPD" },
  { id: "clausula-18", title: "18. Atualização do Termo de Uso" },
  { id: "clausula-19", title: "19. Adendo GEORANKING ADS" },
];

const drawerWidth = 280;

const Section = ({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) => (
  <Box id={id} sx={{ pt: 10, mt: -8, mb: 4 }}>
    <Typography
      variant="h5"
      component="h2"
      gutterBottom
      sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const SubSection = ({ title }: { title: string }) => (
  <Typography
    variant="subtitle1"
    component="h3"
    sx={{ fontWeight: "bold", mt: 2, mb: 0.5 }}
  >
    {title}
  </Typography>
);

export const TermsOfUsePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState(topics[0].id);
  const mainContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      let current = "";
      topics.forEach((topic) => {
        const element = document.getElementById(topic.id);
        if (element && element.getBoundingClientRect().top < 120) {
          current = topic.id;
        }
      });
      setActiveId(current);
    };

    const contentEl = mainContentRef.current;
    contentEl?.addEventListener("scroll", handleScroll);
    return () => contentEl?.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (targetId: string) => {
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (
    <Box>
      <Toolbar />
      <List>
        {topics.map((topic) => (
          <ListItem key={topic.id} disablePadding>
            <ListItemButton
              selected={activeId === topic.id}
              onClick={() => handleNavClick(topic.id)}
            >
              <ListItemText
                primary={topic.title}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: activeId === topic.id ? "bold" : "regular",
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "#ffffff",
          color: "text.primary",
          borderBottom: 1,
          borderColor: "divider",
        }}
        elevation={0}
      >
        <Toolbar>
          {isMobile ? (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{
                mr: 1,
                zIndex: 1001,
                position: "relative",
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <img
              src={gph}
              alt="Logo"
              onClick={() => navigate("/login")}
              style={{ height: 52, marginRight: 16, cursor: "pointer" }}
            />
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Termo de Uso para GeoRanking
          </Typography>
          {!isMobile && (
            <IconButton color="inherit" edge="end" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: "#f7f7f7",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        ref={mainContentRef}
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          overflowY: "auto",
          color: "black",
        }}
      >
        <Toolbar />
        <Container maxWidth="md">
          <Typography variant="caption" display="block" gutterBottom>
            Versão 2.0 — Data de última atualização: 29/06/2026
          </Typography>

          <Section id="qualificacao" title="QUALIFICAÇÃO DAS PARTES">
            <Typography paragraph>
              <strong>FORNECEDOR:</strong> ADS MANAGER, CNPJ 63.460.077/0001-64,
              Terra Office Av. C-4, 931 - Jardim América, Goiânia - GO,
              CEP 74265-040, e-mail: sac@georanking.com.br
            </Typography>
            <Typography paragraph>
              <strong>CONTRATANTE:</strong> Pessoa jurídica ou física que realiza
              o aceite eletrônico do presente Termo por meio da plataforma
              GEORANKING, identificada pelos dados fornecidos no momento do
              cadastro e da contratação.
            </Typography>
            <Typography paragraph>
              As partes acima qualificadas firmam o presente Termo no âmbito de
              relação comercial B2B, destinada ao uso dos serviços como insumo
              da atividade econômica do CONTRATANTE, regida pelo Código Civil
              Brasileiro e pela legislação comercial aplicável, sendo
              expressamente afastada a incidência do Código de Defesa do
              Consumidor (Lei 8.078/1990) em todas as suas disposições.
            </Typography>
          </Section>

          <Section id="clausula-1" title="CLÁUSULA 1 — OBJETO">
            <Typography paragraph>
              1.1 O presente Termo regula a prestação de serviços de marketing
              digital, com finalidade comercial, especificamente a administração
              e gerenciamento de perfis comerciais, com ênfase em serviços de
              Software as a Service (SaaS) destinados à otimização e ao
              impulsionamento do ranqueamento de perfis no Google.
            </Typography>
            <Typography paragraph>
              1.2 Estes serviços incluem o monitoramento da performance e
              automações correlatas, bem como a inserção de até 8 (oito)
              palavras-chave conforme escolha do CONTRATANTE.
            </Typography>
            <Typography paragraph>
              1.3 Os serviços ora prestados pelo FORNECEDOR (ADS MANAGER)
              caracterizam-se como obrigações de meio, não garantindo resultados
              específicos em razão da dependência de diretrizes de terceiros,
              reputação do perfil e uso efetivo por parte do CONTRATANTE. O
              CONTRATANTE contrata os serviços como insumo de sua própria
              atividade econômica, integrando-os ao seu processo produtivo ou
              de atendimento ao mercado.
            </Typography>
            <Typography paragraph>
              1.4 Os serviços poderão abranger diferentes módulos da plataforma
              GEORANKING, incluindo SEO local, gestão de perfis comerciais,
              ranqueamento geográfico, relatórios, automações, monitoramento de
              performance e, quando expressamente contratado, gestão técnica de
              campanhas Google Ads por meio do módulo GEORANKING ADS.
            </Typography>
            <Typography paragraph>
              1.5 O módulo GEORANKING ADS, quando contratado, observará as
              condições específicas previstas na Cláusula 19 deste Termo.
            </Typography>
          </Section>

          <Section id="clausula-2" title="CLÁUSULA 2 — ACEITAÇÃO DO SERVIÇO">
            <Typography paragraph>
              2.1 A aceitação dos serviços será validada mediante autenticação
              eletrônica por meio de login vinculado ao cadastro e marcação do
              checkbox de aceite, meios considerados idôneos para manifestação
              de vontade em contratos entre pessoas jurídicas.
            </Typography>
            <Typography paragraph>
              2.2 O aceite eletrônico realizado pelo CONTRATANTE é plenamente
              válido nos termos do art. 107 do Código Civil Brasileiro, que
              consagra a liberdade de forma nos negócios jurídicos, sendo a
              manifestação de vontade por meios eletrônicos amplamente
              reconhecida na prática comercial e pela jurisprudência como
              suficiente para vinculação contratual entre empresas.
            </Typography>
            <Typography paragraph>
              2.3 O aceite eletrônico será considerado pleno e eficaz para todos
              os fins legais, manifestando a concordância com as condições e
              termos estipulados no presente instrumento.
            </Typography>
            <Typography paragraph>
              2.4 A autenticação eletrônica presume-se realizada pelo próprio
              CONTRATANTE ou por quem este autorize, sendo vedada a alegação de
              desconhecimento ou de fraude que não seja comprovada cabalmente
              nos termos da legislação aplicável.
            </Typography>
            <Typography paragraph>
              2.5 O FORNECEDOR (ADS MANAGER) armazenará registros de aceite
              contendo endereço IP, data/hora (timestamp) e a versão do Termo
              exibida no momento da concordância, para fins de auditoria e prova.
            </Typography>
            <Typography paragraph>
              2.6 O CONTRATANTE deverá manter a regularidade de suas informações
              de acesso, sendo responsável pela veracidade dos dados fornecidos
              e pela segurança de suas credenciais.
            </Typography>
            <Typography paragraph>
              2.7 A responsabilidade pelo uso indevido das credenciais será
              exclusivamente do CONTRATANTE, salvo comprovação de falha de
              segurança originada por atos do FORNECEDOR (ADS MANAGER).
            </Typography>
            <Typography paragraph>
              2.8 Quando o CONTRATANTE contratar o módulo GEORANKING ADS, o
              aceite eletrônico deverá abranger também o Adendo específico
              previsto na Cláusula 19.
            </Typography>
            <Typography paragraph>
              2.9 Para fins de prova e auditoria, o FORNECEDOR poderá registrar
              dados como plano contratado, valor da mensalidade, versão do
              Termo, data e hora do aceite, IP, dispositivo, origem da
              contratação, checkout ID e ID do cliente.
            </Typography>
            <Typography paragraph>
              2.10 A ausência de captura de algum dado técnico isolado não
              invalidará o aceite eletrônico quando houver outros meios idôneos
              de comprovação da manifestação de vontade do CONTRATANTE.
            </Typography>
          </Section>

          <Section
            id="clausula-3"
            title="CLÁUSULA 3 — PERÍODO DE COBRANÇA (ASSINATURA RECORRENTE)"
          >
            <Typography paragraph>
              3.1 A contratação dos serviços opera-se na modalidade de
              assinatura recorrente mensal, processada por intermédio da
              plataforma contratada pelo FORNECEDOR (ADS MANAGER), mediante
              cartão de crédito válido previamente vinculado pelo CONTRATANTE
              e de sua titularidade.
            </Typography>
            <Typography paragraph>
              3.2 O CONTRATANTE autoriza, de forma expressa, a cobrança
              automática e recorrente dos valores devidos no cartão vinculado,
              até que haja cancelamento nos termos deste instrumento.
            </Typography>
            <Typography paragraph>
              3.3 A continuidade da prestação dos serviços fica condicionada à
              autorização e efetiva liquidação das cobranças. Em caso de recusa
              do cartão, insuficiência de limite, chargeback ou qualquer outra
              hipótese de inadimplemento, os serviços poderão ser
              automaticamente suspensos até a regularização, sem prejuízo da
              cobrança de valores vencidos.
            </Typography>
            <Typography paragraph>
              3.4 Em caso de inadimplemento ou rescisão, o FORNECEDOR (ADS
              MANAGER) poderá remover os impulsionamentos e atualizações
              implementadas nos perfis do CONTRATANTE, sem que isso gere
              devolução ou estorno de quaisquer valores, por se tratar de
              benefícios inerentes à prestação do serviço enquanto vigente.
            </Typography>
            <Typography paragraph>
              3.5 Quando contratado o módulo GEORANKING ADS, a cobrança
              recorrente corresponderá exclusivamente à mensalidade de gestão
              técnica, configuração, acompanhamento, otimização, relatórios,
              suporte e uso da plataforma, não se confundindo com verba de mídia.
            </Typography>
            <Typography paragraph>
              3.6 A verba de mídia necessária à veiculação de campanhas Google
              Ads será paga diretamente pelo CONTRATANTE ao Google, por meio
              de conta Google Ads própria.
            </Typography>
            <Typography paragraph>
              3.7 O FORNECEDOR não receberá, custodiará, administrará,
              antecipará, reterá, intermediará ou repassará valores destinados
              à compra de mídia no Google Ads.
            </Typography>
            <Typography paragraph>
              3.8 Cartão recusado, limite insuficiente, ausência de saldo, falha
              de pagamento, bloqueio financeiro, chargeback ou qualquer
              impedimento de cobrança junto ao Google poderá causar pausa,
              restrição ou encerramento da veiculação dos anúncios, sem que
              isso configure falha na prestação dos serviços pelo FORNECEDOR.
            </Typography>
            <Typography paragraph>
              3.9 A autorização de cobrança recorrente prevista neste Termo
              vigora enquanto a assinatura permanecer ativa e até seu
              cancelamento nos termos deste instrumento.
            </Typography>
          </Section>

          <Section
            id="clausula-4"
            title="CLÁUSULA 4 — PERÍODO DE CORTESIA (FACULTATIVO, POR CUPOM)"
          >
            <Typography paragraph>
              4.1 O Período de Cortesia é faculdade exclusiva do FORNECEDOR
              (ADS MANAGER), podendo ser concedido ou não, a seu critério, e
              variar conforme sazonalidade, campanhas de marketing, projetos
              especiais ou para fins de degustação.
            </Typography>
            <Typography paragraph>
              4.2 A concessão do Período de Cortesia será operacionalizada por
              meio de cupom fornecido pelo FORNECEDOR (ADS MANAGER), a ser
              inserido pelo CONTRATANTE no momento do pagamento em campo
              próprio. A não inserção correta do cupom implicará cobrança
              normal do plano.
            </Typography>
            <Typography paragraph>
              4.3 Salvo estipulação diversa na oferta, o Período de Cortesia
              terá duração de 30 (trinta) dias corridos, durante os quais o
              CONTRATANTE poderá usufruir das funcionalidades previstas, sem
              ônus.
            </Typography>
            <Typography paragraph>
              4.4 Ainda que em Cortesia, o acesso aos serviços requer a
              vinculação prévia de um cartão de crédito válido, exclusivamente
              para habilitar a cobrança automática ao término do período, caso
              não haja cancelamento tempestivo.
            </Typography>
            <Typography paragraph>
              4.5 Findo o Período de Cortesia sem cancelamento pelo CONTRATANTE
              até o seu último dia, iniciar-se-á automaticamente a cobrança
              recorrente na forma da Cláusula 3.
            </Typography>
            <Typography paragraph>
              4.6 Caso o CONTRATANTE não deseje prosseguir após a Cortesia,
              deverá cancelar antes do término do período por meio dos canais
              oficiais do FORNECEDOR (ADS MANAGER) e/ou pela opção específica
              no aplicativo, sob pena de iniciar-se a cobrança automática.
            </Typography>
            <Typography paragraph>
              4.7 O Período de Cortesia não gera crédito financeiro e não é
              conversível em valores. Esgotado o período sem contratação da
              assinatura, ou em caso de inadimplemento, poderão ser removidos
              os impulsionamentos e atualizações aplicados, sem estorno.
            </Typography>
            <Typography paragraph>
              4.8 No caso do módulo GEORANKING ADS, eventual cortesia, cupom ou
              desconto será aplicável exclusivamente à mensalidade de gestão ou
              uso da plataforma, não abrangendo verba de mídia, créditos Google
              Ads, custos de terceiros ou produção de criativos especiais.
            </Typography>
          </Section>

          <Section id="clausula-5" title="CLÁUSULA 5 — FORMA DE REAJUSTES">
            <Typography paragraph>
              5.1 Os reajustes nos valores dos serviços serão efetuados
              anualmente, tendo como índice de referência o IPCA ou, na sua
              ausência, outro índice substituto conforme determinação legal.
            </Typography>
            <Typography paragraph>
              5.2 O FORNECEDOR (ADS MANAGER) compromete-se a notificar o
              CONTRATANTE acerca de qualquer alteração no valor da prestação de
              serviços com antecedência mínima de 30 (trinta) dias, em
              observância ao princípio da boa-fé objetiva previsto no art. 422
              do Código Civil Brasileiro.
            </Typography>
            <Typography paragraph>
              5.3 Tratando-se de relação comercial, o cancelamento após o início
              da prestação dos serviços não gera direito à restituição de
              valores já pagos por se tratar de serviços efetivamente
              disponibilizados e utilizados pelo CONTRATANTE. O cancelamento
              produz efeitos a partir do próximo período de cobrança, salvo
              durante o Período de Cortesia previsto na Cláusula 4.
            </Typography>
            <Typography paragraph>
              5.4 Caso o CONTRATANTE opte pelo cancelamento durante o Período de
              Cortesia, este poderá ser realizado sem ônus mediante solicitação
              expressa via aplicativo ou canais oficiais de comunicação, cessando
              a cobrança automática prevista, sem que haja restituição de valores
              por não ter sido cobrada mensalidade durante esse período.
            </Typography>
            <Typography paragraph>
              5.5 Ao FORNECEDOR (ADS MANAGER) é facultado o direito de remover
              quaisquer impulsionamentos realizados no perfil do CONTRATANTE em
              até 15 dias após a efetivação do cancelamento dos serviços.
            </Typography>
            <Typography paragraph>
              5.6 Os reajustes previstos nesta cláusula aplicam-se aos valores
              dos serviços prestados pelo FORNECEDOR, não se aplicando à verba
              de mídia paga diretamente pelo CONTRATANTE ao Google.
            </Typography>
          </Section>

          <Section
            id="clausula-6"
            title="CLÁUSULA 6 — INTERMEDIAÇÃO E TAXA DE ADESÃO"
          >
            <Typography paragraph>
              6.1 O processo de captação e adesão aos serviços será intermediado
              por um agente comercial devidamente credenciado e/ou empregado
              credenciado pelo FORNECEDOR (ADS MANAGER).
            </Typography>
            <Typography paragraph>
              6.2 Este agente será responsável pela execução do atendimento
              inicial ao CONTRATANTE, bem como pela implementação das primeiras
              configurações necessárias ao início dos serviços.
            </Typography>
            <Typography paragraph>
              6.3 A intermediação pelo agente comercial é parte essencial do
              processo de adesão, visando garantir a correta apresentação dos
              serviços e o devido acolhimento das necessidades do CONTRATANTE.
            </Typography>
            <Typography paragraph>
              6.4 O agente comercial credenciado fará jus ao valor
              correspondente à primeira mensalidade do contrato firmado com o
              CONTRATANTE, como remuneração pelos serviços de intermediação e
              configuração inicial prestados. O pagamento deverá ser formalizado
              por meio da emissão de nota fiscal apropriada, em consonância com
              a legislação fiscal vigente.
            </Typography>
            <Typography paragraph>
              6.5 Quando houver contratação do módulo GEORANKING ADS, o agente
              comercial deverá apresentar o serviço como gestão técnica
              recorrente de campanhas, sendo vedada a promessa de vendas, leads,
              faturamento, ROI, posicionamento garantido, anúncios sempre
              ativos, cliques ilimitados ou qualquer resultado específico não
              previsto expressamente neste Termo.
            </Typography>
            <Typography paragraph>
              6.6 As únicas condições comerciais e garantias vinculantes para o
              FORNECEDOR são as expressamente previstas neste Termo, no Adendo
              GEORANKING ADS e na proposta comercial aceita pelo CONTRATANTE.
              Quaisquer declarações ou promessas oferecidas por agente comercial
              que contrariem, ampliem ou modifiquem esses documentos não
              produzem efeitos jurídicos e não vinculam o FORNECEDOR, nos termos
              dos arts. 119 e 679 do Código Civil.
            </Typography>
          </Section>

          <Section id="clausula-7" title="CLÁUSULA 7 — CANCELAMENTO">
            <Typography paragraph>
              7.1 O CONTRATANTE poderá solicitar o cancelamento dos serviços a
              qualquer momento, sem que isso lhe acarrete penalidade ou multa
              pelo ato de cancelamento em si, ressalvada expressamente a hipótese
              de Desconexão Antecipada prevista na Cláusula 9.1, que sujeita o
              CONTRATANTE a penalidade específica em razão do impedimento à
              retirada da propriedade intelectual do FORNECEDOR durante o prazo
              previsto na Cláusula 8.1.
            </Typography>
            <Typography paragraph>
              7.2 Tratando-se de relação comercial, não há direito de
              arrependimento ou estorno de valores já pagos referentes a
              mensalidades em curso, salvo durante o Período de Cortesia
              previsto na Cláusula 4, em que não há cobrança de mensalidade.
            </Typography>
            <Typography paragraph>
              7.3 A solicitação de cancelamento deverá ser feita por meio das
              plataformas disponibilizadas, sendo estas o aplicativo oficial ou
              outros canais de suporte, tais como telefone e WhatsApp.
            </Typography>
            <Typography paragraph>
              7.4 Em caso de cancelamento durante o Período de Cortesia, o
              CONTRATANTE não será cobrado pelas mensalidades subsequentes,
              cessando imediatamente a cobrança recorrente. A remuneração paga
              ao agente comercial pela primeira mensalidade, nos termos da
              Cláusula 6.4, não é reembolsável, por corresponder a serviço de
              intermediação e configuração inicial já prestado.
            </Typography>
            <Typography paragraph>
              7.5 Caso o cancelamento seja solicitado após o início da cobrança
              recorrente, não haverá restituição do valor referente ao período
              corrente, sendo este considerado como devido em razão da
              disponibilidade e execução dos serviços até o momento do
              cancelamento, em conformidade com os arts. 421 e 422 do Código
              Civil.
            </Typography>
            <Typography paragraph>
              7.6 O gerenciamento de perfis comerciais do CONTRATANTE será
              suspenso imediatamente após a confirmação do cancelamento, devendo
              ocorrer a retirada completa dos impulsionamentos em até 15 dias.
            </Typography>
            <Typography paragraph>
              7.7 Especificamente no caso do módulo GEORANKING ADS, o
              cancelamento da mensalidade de gestão não implica cancelamento
              automático de cobranças, faturas, saldo, pendências ou obrigações
              financeiras existentes diretamente entre o CONTRATANTE e o Google
              Ads.
            </Typography>
            <Typography paragraph>
              7.8 Valores cobrados diretamente pelo Google em razão de cliques,
              impressões, conversões, visualizações, testes, campanhas,
              orçamentos diários ou ajustes automáticos são de responsabilidade
              exclusiva do CONTRATANTE.
            </Typography>
            <Typography paragraph>
              7.9 O FORNECEDOR não será responsável por faturas, cobranças,
              tributos, créditos promocionais, estornos, reembolsos, saldo,
              limite, contestação de cobrança ou políticas financeiras aplicadas
              diretamente pelo Google ao CONTRATANTE.
            </Typography>
            <Typography paragraph>
              7.10 Após a confirmação do cancelamento do módulo GEORANKING ADS,
              o FORNECEDOR poderá pausar campanhas sob sua gestão, remover
              acessos operacionais, revogar integrações próprias e retirar
              scripts, tags, dashboards, automações e ativos de propriedade
              intelectual do FORNECEDOR, preservados os dados e ativos de
              titularidade do CONTRATANTE.
            </Typography>
          </Section>

          <Section
            id="clausula-8"
            title="CLÁUSULA 8 — REMOÇÃO DE PROPRIEDADE INTELECTUAL DO FORNECEDOR (ADS MANAGER)"
          >
            <Typography paragraph>
              8.1 O CONTRATANTE reconhece que, em até 5 (cinco) dias contados
              da confirmação do pedido de cancelamento, o FORNECEDOR (ADS
              MANAGER) terá o direito de realizar a retirada integral de toda a
              propriedade intelectual de sua titularidade que tenha sido
              empregada no(s) perfil(is) do CONTRATANTE, incluindo, sem se
              limitar a: códigos, scripts, tags de rastreamento, automações,
              rotinas, templates, criativos, configurações técnicas, integrações
              e metadados inseridos ou habilitados pelo FORNECEDOR, bem como
              credenciais, chaves e senhas eventualmente criadas pelo FORNECEDOR
              exclusivamente para execução dos serviços.
            </Typography>
            <Typography paragraph>
              <strong>Parágrafo 1.</strong> O CONTRATANTE obriga-se a manter o
              acesso técnico ininterrupto e a cooperar com o FORNECEDOR durante
              esse período, abstendo-se de alterar permissões, trocar senhas ou
              revogar acessos que inviabilizem a retirada.
            </Typography>
            <Typography paragraph>
              <strong>Parágrafo 2.</strong> A retirada referida nesta cláusula
              não abrange conteúdos, dados e ativos de titularidade do
              CONTRATANTE, os quais permanecerão sob sua guarda.
            </Typography>
            <Typography paragraph>
              <strong>Parágrafo 3.</strong> Esta cláusula prevalece sobre prazos
              gerais de descontinuidade operacional, exclusivamente no que se
              refere à retirada de propriedade intelectual do FORNECEDOR.
            </Typography>
          </Section>

          <Section
            id="clausula-9"
            title="CLÁUSULA 9 — DESCONEXÃO ANTECIPADA PELO CONTRATANTE E PENALIDADE"
          >
            <Typography paragraph>
              9.1 Se o CONTRATANTE, antes da conclusão do procedimento descrito
              na Cláusula 8.1, desconectar o(s) perfil(is) do SaaS, revogar
              acessos, alterar senhas/permissões, mudar o "owner" dos ativos,
              remover integrações, excluir tags/scripts ou de qualquer forma
              impedir a retirada integral da propriedade intelectual do
              FORNECEDOR (doravante, "Desconexão Antecipada"), ficará sujeito ao
              pagamento de pena não compensatória equivalente ao valor de 5
              (cinco) mensalidades vigentes na data da Desconexão Antecipada,
              devida em 10 (dez) dias contados da notificação do FORNECEDOR,
              sem prejuízo da apuração de eventuais perdas e danos suplementares.
              O valor da pena é fixado em consonância com o princípio da
              proporcionalidade previsto no art. 413 do Código Civil.
            </Typography>
            <Typography paragraph>
              <strong>Parágrafo Único.</strong> A penalidade ora prevista tem
              natureza autônoma e incide independentemente de outras medidas
              cabíveis, inclusive a adoção de providências técnicas para proteção
              dos direitos de propriedade intelectual do FORNECEDOR.
            </Typography>
          </Section>

          <Section id="clausula-10" title="CLÁUSULA 10 — PROIBIÇÕES DE USO">
            <Typography paragraph>
              10.1 Fica expressamente proibido ao CONTRATANTE utilizar os
              serviços de forma ilícita, fraudulenta ou difamatória.
            </Typography>
            <Typography paragraph>
              10.2 Tal proibição inclui, mas não se limita, a atos que possam
              violar qualquer legislação penal vigente ou resultar em violação
              de direitos de terceiros.
            </Typography>
            <Typography paragraph>
              10.3 O CONTRATANTE deverá abster-se de utilizar a plataforma para
              fins ofensivos, prejudiciais ou que infrinjam normas legais, sob
              pena de incorrer nas sanções previstas neste instrumento e na
              legislação aplicável.
            </Typography>
            <Typography paragraph>
              10.4 É vedado ao CONTRATANTE burlar ou tentar burlar os limites
              de uso dos serviços estipulados pelo FORNECEDOR (ADS MANAGER).
            </Typography>
            <Typography paragraph>
              10.5 A tentativa ou efetivação de quaisquer ações que visem
              modificar, adaptar, traduzir ou interferir com o funcionamento da
              plataforma sem autorização expressa e por escrito do FORNECEDOR
              (ADS MANAGER) será considerada violação grave deste contrato,
              passível de rescisão.
            </Typography>
            <Typography paragraph>
              10.6 Qualquer tentativa de engenharia reversa, descompilação ou
              cópia não autorizada de qualquer parte da plataforma constitui
              violação dos direitos de propriedade intelectual do FORNECEDOR
              (ADS MANAGER).
            </Typography>
            <Typography paragraph>
              10.7 O CONTRATANTE compromete-se a não tomar qualquer ação que
              comprometa a segurança, integridade ou disponibilidade da
              plataforma.
            </Typography>
            <Typography paragraph>
              10.8 Em caso de uso malicioso ou contrário aos termos ora
              estabelecidos, o CONTRATANTE estará sujeito a multa não
              compensatória no valor de R$ 10.000,00 (dez mil reais), sem
              prejuízo de outras sanções, como o cancelamento automático dos
              serviços e eventual responsabilização por perdas e danos.
            </Typography>
            <Typography paragraph>
              10.9 O FORNECEDOR (ADS MANAGER) reserva-se o direito de adotar
              todas as medidas judiciais e extrajudiciais necessárias para
              resguardar seus direitos e interesses.
            </Typography>
            <Typography paragraph>
              10.10 No uso do módulo GEORANKING ADS, o CONTRATANTE
              compromete-se a não solicitar anúncios, segmentações, textos,
              criativos ou práticas que sejam ilegais, enganosas, abusivas,
              discriminatórias, ofensivas, fraudulentas ou que violem direitos
              de terceiros, políticas do Google Ads ou normas setoriais.
            </Typography>
            <Typography paragraph>
              10.11 O CONTRATANTE é responsável pela regularidade de sua
              atividade econômica, licenças, alvarás, autorizações, veracidade
              das informações, legalidade dos produtos e serviços anunciados e
              atendimento ao mercado.
            </Typography>
            <Typography paragraph>
              10.12 Caso o CONTRATANTE atue em segmento regulado, restrito ou
              sensível, deverá informar previamente ao FORNECEDOR e apresentar,
              quando solicitado, documentos, licenças, registros ou autorizações
              necessários para veiculação de anúncios.
            </Typography>
            <Typography paragraph>
              10.13 O FORNECEDOR poderá recusar, pausar ou solicitar ajustes em
              campanhas, anúncios, textos ou criativos que possam violar leis,
              políticas de plataforma, direitos de terceiros ou expor o
              FORNECEDOR a risco jurídico, reputacional, comercial ou operacional.
            </Typography>
          </Section>

          <Section
            id="clausula-11"
            title="CLÁUSULA 11 — LIMITAÇÃO DE RESPONSABILIDADE"
          >
            <Typography paragraph>
              11.1 O FORNECEDOR (ADS MANAGER) não responderá por lucros
              cessantes, danos indiretos, especiais, exemplares, punitivos ou
              consequentes, ainda que previsíveis, relacionados ao uso da
              Plataforma e dos serviços prestados, nos termos dos princípios
              gerais do direito contratual e do Código Civil Brasileiro. As
              partes, na presente relação comercial B2B, assumem os riscos de
              sua atividade econômica e contratam com plena consciência das
              limitações inerentes a serviços de marketing digital.
            </Typography>
            <Typography paragraph>
              11.2 O FORNECEDOR (ADS MANAGER) não se responsabiliza por
              quaisquer perdas, danos, limitações de uso, indisponibilidades,
              suspensões, remoções, reprovações, restrições ou bloqueios que
              decorram, direta ou indiretamente, de: (a) alterações de
              políticas, diretrizes ou critérios algorítmicos do Google; (b)
              suspensão, limitação, remoção ou despublicação de perfis, páginas,
              fichas, campanhas ou anúncios decididas unilateralmente por
              plataformas de terceiros; (c) reputação on-line do CONTRATANTE ou
              conteúdo inserido pelo CONTRATANTE ou por terceiros; (d) falhas,
              indisponibilidades ou instabilidades de provedores externos; (e)
              atos ou omissões de Agentes de Negócios credenciados quando
              atuarem fora das diretrizes fornecidas pelo FORNECEDOR; (f) ordens,
              solicitações ou determinações de autoridades públicas ou judiciais.
            </Typography>
            <Typography paragraph>
              11.3 Em consonância com os arts. 421 e 422 do Código Civil, o
              limite de responsabilidade do FORNECEDOR (ADS MANAGER) por danos
              diretos não excederá, em hipótese alguma, o montante total
              efetivamente pago pelo CONTRATANTE ao FORNECEDOR nos 3 (três)
              meses imediatamente anteriores à ocorrência do evento gerador da
              responsabilidade.
            </Typography>
            <Typography paragraph>
              11.4 As partes reconhecem que o presente acordo reflete a alocação
              de riscos expressamente negociada e acordada no contexto de
              relação comercial.
            </Typography>
            <Typography paragraph>
              11.5 Qualquer tentativa de aumento de responsabilidade do
              FORNECEDOR (ADS MANAGER) por meio de alegações contrárias ao
              estabelecido nesta cláusula será respondida com o exercício regular
              dos meios de defesa processual cabíveis nos termos da legislação
              vigente.
            </Typography>
            <Typography paragraph>
              11.6 A presente cláusula não se aplica em casos de dolo, fraude ou
              qualquer conduta intencionalmente prejudicial por parte do
              FORNECEDOR (ADS MANAGER), hipóteses em que a responsabilidade será
              apurada de acordo com as disposições legais específicas aplicáveis.
            </Typography>
            <Typography paragraph>
              11.7 Especificamente para campanhas Google Ads, o FORNECEDOR não
              será responsável por ausência de vendas, lucro, faturamento,
              volume insuficiente de leads, baixa taxa de conversão, custo
              elevado por clique ou lead, reprovação de anúncios, suspensão,
              bloqueio ou limitação de conta, indisponibilidade do Google Ads,
              falhas no site, telefone, WhatsApp ou canais do CONTRATANTE, mau
              atendimento, oferta pouco competitiva, concorrência, sazonalidade,
              orçamento insuficiente, cliques inválidos ou decisões tomadas pelo
              CONTRATANTE contra recomendação técnica do FORNECEDOR.
            </Typography>
          </Section>

          <Section id="clausula-12" title="CLÁUSULA 12 — DISPOSIÇÕES GERAIS">
            <Typography paragraph>
              12.1 Este Termo de Uso constitui o acordo integral entre as partes,
              prevalecendo sobre quaisquer entendimentos, acordos ou propostas
              anteriores, verbais ou escritos, relacionados ao objeto deste
              contrato.
            </Typography>
            <Typography paragraph>
              12.2 Quaisquer modificações ou alterações neste Termo somente terão
              validade se formalizadas por escrito e devidamente aceitas por
              ambas as partes, nos termos da legislação vigente, ressalvando-se
              a possibilidade de alteração por meio eletrônico nos termos do
              art. 107 do Código Civil.
            </Typography>
            <Typography paragraph>
              12.3 Na eventualidade de qualquer disposição deste contrato ser
              considerada inválida ou inexequível por tribunal competente, tal
              invalidade não afetará outras disposições deste contrato, que
              permanecerão em pleno vigor e efeito.
            </Typography>
            <Typography paragraph>
              12.4 As partes se comprometem a negociar de boa-fé a substituição
              da disposição inválida ou inexequível por uma disposição válida
              que alcance os efeitos econômicos pretendidos pela disposição
              original.
            </Typography>
            <Typography paragraph>
              12.5 Este Termo é regido e interpretado de acordo com as leis
              vigentes na República Federativa do Brasil, em especial o Código
              Civil (Lei 10.406/2002) e a legislação comercial aplicável.
            </Typography>
          </Section>

          <Section
            id="clausula-13"
            title="CLÁUSULA 13 — FORO E RESOLUÇÃO DE CONFLITOS"
          >
            <Typography paragraph>
              13.1 As partes elegem, de comum acordo, o foro da Comarca de
              Goiânia, Estado de Goiás, como único e exclusivo foro competente
              para dirimir quaisquer questões oriundas deste Termo ou de sua
              execução, renunciando expressamente a qualquer outro foro, por mais
              privilegiado que seja. A eleição de foro é plenamente válida no
              contexto de relação comercial, não estando sujeita às restrições
              aplicáveis a relações de consumo.
            </Typography>
            <Typography paragraph>
              13.2 Antes de recorrer ao Poder Judiciário, as partes poderão, de
              forma facultativa e de comum acordo, buscar solução alternativa de
              controvérsia por meio de mediação ou conciliação, visando a
              celeridade e a preservação da relação comercial.
            </Typography>
            <Typography paragraph>
              13.3 Eventuais disputas derivadas do presente Termo serão
              dirimidas exclusivamente no foro da Comarca de Goiânia-GO, sendo
              vedada a proposição de demanda em qualquer outro juízo ou tribunal,
              ressalvadas as hipóteses legais de competência absoluta.
            </Typography>
            <Typography paragraph>
              13.4 Em caso de nulidade parcial de qualquer disposição desta
              cláusula, a validade das demais condições não será afetada,
              devendo as partes buscar a retificação do ponto específico,
              preservando a intenção originalmente acordada.
            </Typography>
          </Section>

          <Section
            id="clausula-14"
            title="CLÁUSULA 14 — CANAL DE SUPORTE AO CONTRATANTE"
          >
            <Typography paragraph>
              14.1 O FORNECEDOR (ADS MANAGER) disponibilizará ao CONTRATANTE
              canais oficiais de suporte técnico e atendimento durante o horário
              comercial, de segunda a sexta-feira, das 9h às 18h (horário de
              Brasília).
            </Typography>
            <Typography paragraph>
              14.2 O atendimento poderá ser realizado por meio de telefone ou
              WhatsApp (0800 800 1700), observando-se a ordem de prioridade e a
              natureza da solicitação.
            </Typography>
            <Typography paragraph>
              14.3 O FORNECEDOR compromete-se a responder às demandas do
              CONTRATANTE dentro de um prazo de 72 horas úteis, respeitando a
              complexidade do atendimento, não se responsabilizando por atrasos
              decorrentes de fatores externos ou imprevistos técnicos.
            </Typography>
            <Typography paragraph>
              14.4 No caso do módulo GEORANKING ADS, poderão ser objeto de
              suporte questões como dúvidas sobre campanha, alteração de
              anúncios, reprovação de anúncios, campanha pausada, orçamento
              esgotado, falha de pagamento junto ao Google, problema de tag ou
              conversão, solicitação de relatório, revisão de estratégia e
              suspeita de suspensão ou limitação de conta.
            </Typography>
            <Typography paragraph>
              14.5 Quando a solução depender de análise, revisão ou decisão do
              Google ou de outra plataforma de terceiros, o FORNECEDOR não
              garantirá prazo de resolução, comprometendo-se apenas a adotar
              esforços razoáveis dentro de sua esfera técnica de atuação.
            </Typography>
          </Section>

          <Section
            id="clausula-15"
            title="CLÁUSULA 15 — DIVULGAÇÃO DE RESULTADOS E PORTFÓLIO"
          >
            <Typography paragraph>
              15.1 O CONTRATANTE autoriza, de forma não exclusiva, que o
              FORNECEDOR divulgue em materiais promocionais, websites, redes
              sociais e relatórios públicos os resultados obtidos com os serviços
              prestados de impulsionamento digital, incluindo indicadores de
              ranqueamento e performance alcançados.
            </Typography>
            <Typography paragraph>
              15.2 O FORNECEDOR poderá mencionar o nome, marca e logotipo do
              CONTRATANTE para fins de demonstração de portfólio e comprovação
              de eficácia técnica, desde que observados os limites legais de uso
              da imagem e marca.
            </Typography>
            <Typography paragraph>
              15.3 O CONTRATANTE poderá revogar a autorização a qualquer tempo
              pelos canais oficiais. O FORNECEDOR terá até 15 dias para cessar
              novas divulgações; conteúdos já publicados poderão permanecer
              arquivados por requisitos de prova e compliance.
            </Typography>
            <Typography paragraph>
              15.4 Em relação ao módulo GEORANKING ADS, o FORNECEDOR poderá
              utilizar dados anonimizados, agregados ou estatísticos de campanhas
              para fins de estudo, melhoria de produto, demonstração comercial,
              treinamento, portfólio e materiais institucionais.
            </Typography>
            <Typography paragraph>
              15.5 A divulgação pública de nome, marca, logotipo, investimento,
              faturamento, quantidade de leads, prints individualizados,
              relatórios identificáveis ou resultados específicos do CONTRATANTE
              em campanhas Google Ads dependerá de autorização prévia, específica
              e rastreável.
            </Typography>
          </Section>

          <Section
            id="clausula-16"
            title="CLÁUSULA 16 — PROPRIEDADE INTELECTUAL"
          >
            <Typography paragraph>
              16.1 Todos os direitos de propriedade intelectual relativos à
              tecnologia desenvolvida, sistemas, códigos-fonte, algoritmos,
              design, UI/UX, processos técnicos, metodologias e know-how
              utilizados na execução dos serviços e na operação da plataforma são
              de titularidade exclusiva do FORNECEDOR (ADS MANAGER).
            </Typography>
            <Typography paragraph>
              16.2 É expressamente vedada ao CONTRATANTE a cópia, reprodução,
              modificação, engenharia reversa, disponibilização a terceiros, ou
              qualquer forma de uso não autorizado do conteúdo intelectual
              pertencente ao FORNECEDOR (ADS MANAGER).
            </Typography>
            <Typography paragraph>
              16.3 O descumprimento desta cláusula acarretará responsabilidade
              civil e criminal, conforme a legislação vigente sobre direitos
              autorais e propriedade intelectual.
            </Typography>
            <Typography paragraph>
              16.4 No módulo GEORANKING ADS, pertencem ao FORNECEDOR todos os
              direitos relativos à metodologia, processos técnicos, know-how,
              sistemas, códigos, dashboards, automações, scripts, templates,
              estruturas internas, rotinas, documentos estratégicos, padrões de
              campanha e integrações proprietárias.
            </Typography>
            <Typography paragraph>
              16.5 Pertencem ao CONTRATANTE, salvo disposição comercial
              específica em contrário, sua marca, nome comercial, domínio, dados
              comerciais, leads, contatos, clientes, produtos, serviços, conta
              própria de Google Ads quando criada ou mantida em sua titularidade
              e dados de performance relacionados ao seu negócio.
            </Typography>
            <Typography paragraph>
              16.6 O encerramento da contratação não transfere ao CONTRATANTE
              propriedade sobre metodologias, automações, scripts, templates,
              integrações, estruturas técnicas ou know-how do FORNECEDOR, nem
              autoriza o FORNECEDOR a reter indevidamente dados, leads ou ativos
              de titularidade do CONTRATANTE.
            </Typography>
          </Section>

          <Section
            id="clausula-17"
            title="CLÁUSULA 17 — POLÍTICA DE PRIVACIDADE E LGPD"
          >
            <Typography paragraph>
              17.1 O CONTRATANTE declara estar ciente e de acordo que, ao
              confirmar a contratação e utilização dos serviços ofertados, aceita
              automaticamente a Política de Privacidade do FORNECEDOR (ADS
              MANAGER), disponível nos mesmos canais utilizados para a
              contratação.
            </Typography>
            <Typography paragraph>
              17.2 A Política de Privacidade integra este Termo de Uso para
              todos os fins de direito, obrigando ambas as partes quanto às
              diretrizes de tratamento e proteção de dados pessoais em
              conformidade com a Lei 13.709/2018 (LGPD).
            </Typography>
            <Typography paragraph>
              17.3 Para execução dos serviços, incluindo o módulo GEORANKING
              ADS, poderão ser tratados dados pessoais relacionados a leads,
              formulários, ligações, mensagens, eventos de conversão, navegação,
              remarketing, públicos personalizados, relatórios e demais
              informações necessárias à operação contratada.
            </Typography>
            <Typography paragraph>
              17.4 O FORNECEDOR atuará como controlador dos dados pessoais de
              seus clientes, leads e contatos, cabendo-lhe definir as finalidades
              do tratamento, possuir base legal adequada e assegurar a
              regularidade da coleta e uso desses dados.
            </Typography>
            <Typography paragraph>
              17.5 O FORNECEDOR atuará como operador dos dados pessoais quando
              realizar tratamento em nome do CONTRATANTE para execução das
              campanhas, relatórios, integrações, suporte e demais serviços
              contratados.
            </Typography>
            <Typography paragraph>
              17.6 O CONTRATANTE compromete-se a não fornecer ao FORNECEDOR
              listas, públicos, bases de contatos, dados sensíveis ou informações
              pessoais obtidas de forma ilícita, irregular ou sem base legal
              adequada.
            </Typography>
            <Typography paragraph>
              17.7 O FORNECEDOR registrará as operações de tratamento de dados
              pessoais conforme exigido pelo art. 37 da LGPD, indicando as
              finalidades, bases legais, categorias de dados tratados, prazos de
              retenção e destinatários, inclusive em caso de transferência
              internacional de dados para plataformas de terceiros, como o
              Google, adotando as salvaguardas previstas no art. 33 da LGPD.
            </Typography>
            <Typography paragraph>
              17.8 O canal para exercício dos direitos dos titulares de dados
              pessoais previstos no art. 18 da LGPD, bem como o contato do
              encarregado pelo tratamento de dados (DPO), estão indicados na
              Política de Privacidade do FORNECEDOR.
            </Typography>
          </Section>

          <Section
            id="clausula-18"
            title="CLÁUSULA 18 — ATUALIZAÇÃO DO TERMO DE USO"
          >
            <Typography paragraph>
              18.1 O FORNECEDOR (ADS MANAGER) poderá atualizar o presente Termo
              de Uso a qualquer tempo, mediante notificação prévia ao
              CONTRATANTE com antecedência mínima de 7 (sete) dias.
            </Typography>
            <Typography paragraph>
              18.2 A notificação será realizada pelos canais oficiais de
              comunicação e suporte previstos neste instrumento.
            </Typography>
            <Typography paragraph>
              18.3 A continuidade do uso dos serviços após o prazo estabelecido
              na notificação implicará concordância tácita e aceite integral das
              novas condições, disposição válida no contexto da relação comercial
              entre as partes.
            </Typography>
            <Typography paragraph>
              18.4 Alterações materiais que envolvam preço, objeto principal,
              recorrência, cancelamento, tratamento de dados, módulos contratados
              ou condições específicas do GEORANKING ADS deverão ser comunicadas
              de forma clara ao CONTRATANTE, sendo-lhe assegurado o direito de
              cancelamento sem ônus quando a alteração for unilateral e não
              aceita.
            </Typography>
          </Section>

          <Section
            id="clausula-19"
            title="CLÁUSULA 19 — ADENDO ESPECÍFICO DO MÓDULO GEORANKING ADS"
          >
            <SubSection title="19.1 — Objeto" />
            <Typography paragraph>
              19.1.1 Este Adendo integra o presente Termo de Uso e regula as
              condições específicas aplicáveis à contratação do módulo GEORANKING
              ADS, destinado à gestão técnica de campanhas Google Ads e tráfego
              pago, no contexto de relação comercial B2B entre o FORNECEDOR (ADS
              MANAGER) e o CONTRATANTE.
            </Typography>
            <Typography paragraph>
              19.1.2 O módulo GEORANKING ADS tem por objeto a prestação de
              serviços de gestão técnica, planejamento, configuração,
              acompanhamento, otimização e suporte relacionados a campanhas de
              mídia paga, especialmente na plataforma Google Ads.
            </Typography>

            <SubSection title="19.2 — Natureza dos Serviços" />
            <Typography paragraph>
              19.2.1 Os serviços do GEORANKING ADS possuem natureza técnica,
              consultiva e operacional, caracterizando-se como obrigação de meio,
              e não como obrigação de resultado. O FORNECEDOR não garante vendas,
              faturamento, lucro, volume mínimo de leads, retorno sobre
              investimento, custo por clique, custo por conversão, posição
              específica, aprovação obrigatória dos anúncios, exibição contínua
              das campanhas ou qualquer resultado comercial específico.
            </Typography>
            <Typography paragraph>
              19.2.2 O CONTRATANTE declara ciência de que os resultados de
              campanhas Google Ads dependem de fatores externos ao controle
              direto do FORNECEDOR, incluindo orçamento disponível, concorrência,
              leilão, região, público, qualidade da oferta, página de destino,
              reputação, atendimento comercial, sazonalidade, políticas da
              plataforma Google Ads e comportamento do mercado.
            </Typography>

            <SubSection title="19.3 — Mensalidade de Gestão e Mídia" />
            <Typography paragraph>
              19.3.1 A mensalidade paga pelo CONTRATANTE ao FORNECEDOR remunera
              exclusivamente os serviços de gestão técnica, planejamento,
              configuração, acompanhamento, otimização, suporte e uso do módulo
              GEORANKING ADS.
            </Typography>
            <Typography paragraph>
              19.3.2 A mensalidade de gestão não se confunde, em nenhuma
              hipótese, com verba de mídia, crédito publicitário, saldo de
              anúncios ou pagamento destinado à plataforma Google Ads.
            </Typography>
            <Typography paragraph>
              19.3.3 A verba de mídia necessária à veiculação dos anúncios será
              paga exclusivamente e diretamente pelo CONTRATANTE ao Google, por
              meio de conta Google Ads própria.
            </Typography>
            <Typography paragraph>
              19.3.4 O FORNECEDOR não receberá, custodiará, administrará,
              antecipará, reterá, intermediará ou repassará valores destinados à
              compra de mídia no Google Ads.
            </Typography>
            <Typography paragraph>
              19.3.5 O CONTRATANTE declara ciência de que a continuidade da
              veiculação dos anúncios depende da existência de saldo, limite,
              crédito, forma de pagamento válida ou cobrança regular diretamente
              junto ao Google Ads. Falhas no pagamento junto ao Google são de
              responsabilidade exclusiva do CONTRATANTE e não configuram
              inadimplemento do FORNECEDOR.
            </Typography>

            <SubSection title="19.4 — Transparência e Acesso aos Dados" />
            <Typography paragraph>
              19.4.1 O FORNECEDOR disponibilizará ao CONTRATANTE, conforme o
              plano contratado, informações periódicas de desempenho das
              campanhas, podendo incluir custo de mídia, cliques, impressões,
              conversões quando configuradas e mensuráveis, custo médio por
              clique, custo por conversão e recomendações de otimização.
            </Typography>
            <Typography paragraph>
              19.4.2 O relatório poderá ser disponibilizado por meio da
              plataforma GEORANKING, dashboard, e-mail, WhatsApp, reunião,
              arquivo digital ou outro canal definido operacionalmente pelo
              FORNECEDOR.
            </Typography>
            <Typography paragraph>
              19.4.3 Sempre que solicitado pelo CONTRATANTE, o FORNECEDOR
              informará o ID da conta Google Ads utilizada nas campanhas.
            </Typography>
            <Typography paragraph>
              19.4.4 O acesso do CONTRATANTE aos dados de sua campanha observará
              a seguinte distinção: (I) dados de performance — cliques,
              impressões, conversões, custos e demais resultados operacionais —
              são de acesso irrestrito do CONTRATANTE, que poderá solicitá-los
              a qualquer momento pelos canais de suporte; (II) a metodologia,
              dashboards proprietários, automações, scripts, códigos e
              configurações internas do FORNECEDOR são de sua exclusiva
              propriedade intelectual e não estão sujeitos a acesso ou divulgação
              ao CONTRATANTE.
            </Typography>

            <SubSection title="19.5 — Aceite Eletrônico do Adendo" />
            <Typography paragraph>
              19.5.1 A contratação do módulo GEORANKING ADS poderá ocorrer por
              meio eletrônico, incluindo site, aplicativo, checkout, link de
              pagamento, formulário digital, área do cliente, WhatsApp, e-mail,
              ferramenta de assinatura eletrônica ou outro meio disponibilizado
              pelo FORNECEDOR.
            </Typography>
            <Typography paragraph>
              19.5.2 O aceite eletrônico será considerado válido, nos termos do
              art. 107 do Código Civil, quando o CONTRATANTE manifestar
              concordância por meio de ação positiva, incluindo marcação de
              checkbox, clique em botão de aceite, confirmação em checkout,
              assinatura eletrônica, autenticação por login, confirmação expressa
              por canal digital ou pagamento da primeira cobrança.
            </Typography>
            <Typography paragraph>
              19.5.3 A ausência de captura de algum dado técnico específico não
              invalidará o aceite eletrônico quando houver outros meios idôneos
              de comprovação da manifestação de vontade do CONTRATANTE.
            </Typography>

            <SubSection title="19.6 — Políticas do Google Ads" />
            <Typography paragraph>
              19.6.1 O CONTRATANTE declara ciência de que a veiculação de
              anúncios depende das políticas, aprovações, revisões, sistemas,
              algoritmos, critérios comerciais e decisões da plataforma Google
              Ads.
            </Typography>
            <Typography paragraph>
              19.6.2 O Google poderá, a qualquer tempo e a seu exclusivo
              critério, reprovar anúncios, limitar campanhas, exigir verificação
              do anunciante, suspender contas, bloquear criativos ou alterar
              regras.
            </Typography>
            <Typography paragraph>
              19.6.3 Tais eventos não caracterizam inadimplemento do FORNECEDOR
              quando decorrerem de decisão da plataforma, alteração de política,
              risco de compliance, conteúdo do CONTRATANTE ou qualquer fator
              externo ao controle direto do FORNECEDOR.
            </Typography>
            <Typography paragraph>
              19.6.4 O FORNECEDOR poderá realizar esforços razoáveis para
              ajustar, corrigir, reenviar ou adaptar anúncios e campanhas, sem
              garantia de aprovação ou reativação pela plataforma.
            </Typography>

            <SubSection title="19.7 — Não Vinculação ao Google" />
            <Typography paragraph>
              19.7.1 O GEORANKING e o FORNECEDOR (ADS MANAGER) não se confundem
              com o Google, Google Ads, Google Business Profile, Google Meu
              Negócio ou qualquer outra plataforma de terceiros.
            </Typography>
            <Typography paragraph>
              19.7.2 O FORNECEDOR não atua em nome do Google, não representa o
              Google e não possui controle sobre decisões unilaterais tomadas
              pela plataforma Google Ads.
            </Typography>
            <Typography paragraph>
              19.7.3 Eventual menção a certificações, selos ou programas de
              parceiros terá finalidade exclusivamente informativa, não
              representando garantia de aprovação, privilégio operacional ou
              tratamento diferenciado dentro da plataforma Google Ads.
            </Typography>

            <SubSection title="19.8 — Cancelamento do Módulo GEORANKING ADS" />
            <Typography paragraph>
              19.8.1 O CONTRATANTE poderá solicitar o cancelamento do módulo
              GEORANKING ADS pelos canais oficiais de atendimento disponibilizados
              pelo FORNECEDOR.
            </Typography>
            <Typography paragraph>
              19.8.2 Tratando-se de relação comercial, não há direito de
              arrependimento. O cancelamento produz efeitos a partir do próximo
              período de cobrança, não havendo restituição de mensalidade
              referente ao período corrente quando os serviços já tiverem sido
              disponibilizados, iniciados ou executados, ainda que parcialmente.
            </Typography>
            <Typography paragraph>
              19.8.3 O cancelamento da mensalidade de gestão não implica
              cancelamento automático de cobranças, faturas, saldo, pendências
              ou obrigações financeiras existentes diretamente entre o
              CONTRATANTE e o Google Ads.
            </Typography>
            <Typography paragraph>
              19.8.4 Após a confirmação do cancelamento, o FORNECEDOR poderá
              pausar campanhas, remover acessos operacionais, revogar integrações
              próprias, retirar scripts, tags, dashboards, automações e ativos de
              propriedade intelectual do FORNECEDOR, preservados os dados e
              ativos de titularidade do CONTRATANTE.
            </Typography>
            <Typography paragraph>
              19.8.5 Caso o CONTRATANTE remova acessos, revogue permissões,
              altere senhas, desconecte integrações ou impeça a retirada técnica
              de ativos proprietários do FORNECEDOR, incidirá a penalidade
              prevista na Cláusula 9.1, desde que comprovado prejuízo ou
              impedimento indevido.
            </Typography>

            <SubSection title="19.9 — Disposições Finais do Adendo" />
            <Typography paragraph>
              19.9.1 Este Adendo prevalecerá sobre o Termo de Uso principal
              apenas naquilo que tratar especificamente do módulo GEORANKING ADS,
              permanecendo válidas as demais disposições não conflitantes.
            </Typography>
            <Typography paragraph>
              19.9.2 Em caso de divergência entre a proposta comercial aceita,
              o plano contratado e este Adendo, prevalecerá a condição mais
              específica, desde que aceita pelo CONTRATANTE e compatível com a
              legislação aplicável.
            </Typography>
            <Typography paragraph>
              19.9.3 O uso continuado do módulo GEORANKING ADS, após aceite
              eletrônico, pagamento, liberação de acesso ou início da prestação
              dos serviços, implica ciência e concordância com as condições aqui
              estabelecidas.
            </Typography>
          </Section>
        </Container>
      </Box>
    </Box>
  );
};
